<template>
  <section class="review-section" :class="`theme-${track}`">
    <div class="layout-content">
      <header class="section-heading">
        <h2>{{ isEnglish ? 'Judging Criteria' : '评审规则' }}</h2>
        <span>{{ reviewIntro }}</span>
      </header>

      <div class="review-grid" :class="{ 'review-grid--split': track === 'domestic' }">
        <article v-for="panel in panels" :key="panel.key" class="review-panel">
          <header>
            <h3>{{ panel.title }}</h3>
          </header>
          <div v-if="panel.criteria" class="criteria-list">
            <div v-for="item in panel.criteria" :key="item.title" class="criterion">
              <strong>{{ item.percent }}</strong>
              <div><h4>{{ item.title }}</h4><p v-if="item.text">{{ item.text }}</p></div>
            </div>
          </div>
          <div v-for="block in panel.blocks || []" :key="block.title" class="review-block" :class="{ pending:block.pending }">
            <h4>{{ block.title }}</h4>
            <p>{{ block.text }}</p>
            <div v-if="block.formula" class="formula">{{ block.formula }}</div>
            <ul v-if="block.items"><li v-for="item in block.items" :key="item">{{ item }}</li></ul>
          </div>
          <p v-if="panel.note" class="panel-note">{{ panel.note }}</p>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'

export default {
  props: {
    track: { type:String, default:'domestic' },
    language: { type:String, default:'zh' }
  },
  computed: {
    ...mapState(['isEnLang']),
    isEnglish () { return this.isEnLang || (this.track === 'international' && this.language === 'en') },
    reviewIntro () {
      if (this.isEnglish) return this.track === 'domestic' ? 'Each domestic track follows criteria appropriate to its competition format.' : 'All six International Track topics use the same core judging dimensions.'
      return this.track === 'domestic' ? '国内两个赛道根据各自作品类型执行相应评审规则。' : '国际赛道六个方向共用统一核心评分维度，确保跨方向可比。'
    },
    panels () { return this.track === 'international' ? [this.internationalPanel] : this.domesticPanels },
    domesticPanels () {
      if (this.isEnglish) return [
        { key:'ai', index:'01', eyebrow:'DOMESTIC TRACK', title:'Artificial Intelligence Track', criteria:[
          { percent:'40%', title:'Task Completion', text:'Completion of scripted financial tasks, quality of planning and correctness of tool use.' },
          { percent:'30%', title:'Security & Compliance', text:'Defence against induced transfers, information extraction, privilege escalation and prompt injection; confirmation and risk warnings for sensitive operations.' },
          { percent:'30%', title:'Innovation & Interaction', text:'Novelty, real-world value, dialogue efficiency, clarification, error recovery and handling of colloquial input.' }
        ] },
        { key:'data', index:'02', eyebrow:'DOMESTIC TRACK', title:'Data Analytics Track', criteria:[
          { percent:'50%', title:'Data Understanding & Modelling Analysis', text:'Assesses the completeness of data introduction and the quality of data cleaning; the depth of business-pain-point discovery and visual communication in descriptive analysis; and the rationale of feature engineering and model selection, rigour of algorithmic logic, and standardisation of the modelling process.' },
          { percent:'30%', title:'Application Validation & Effect Evaluation', text:'Assesses the scientific soundness of the validation plan, such as offline evaluation, back-testing and cross-validation; whether evaluation metrics fit the business context; model interpretability; and robustness in real-world environments.' },
          { percent:'20%', title:'Product Thinking & Innovation Value', text:'Assesses the feasibility of scenario implementation, the logical closed loop of the product solution and interaction design; the originality of the problem-solving perspective, methodological transfer or integration, and potential value and scalability in real financial or business scenarios.' }
        ] }
      ]
      return [
        { key:'ai', index:'01', eyebrow:'国内赛道', title:'人工智能赛道', criteria:[
          { percent:'40%', title:'任务完成率', text:'考察典型金融场景任务的完成情况、多步骤任务规划合理性及工具调用正确性。' },
          { percent:'30%', title:'安全合规性', text:'考察对诱导转账、信息套取、越权操作和 Prompt 注入等风险的防御能力，以及敏感操作确认和风险提示。' },
          { percent:'30%', title:'创新性及交互体验', text:'考察场景新颖性、真实问题解决度、对话轮次效率、追问能力、错误恢复、口语化输入和超范围兜底。' }
        ] },
        { key:'data', index:'02', eyebrow:'国内赛道', title:'数据分析赛道', criteria:[
          { percent:'50%', title:'数据理解与建模分析', text:'考察数据介绍的完整性与清洗规范性；考察描述性分析对业务痛点的挖掘深度与可视化表达；考察特征工程与模型选择的合理性、算法逻辑的严谨性及建模流程的标准化程度。' },
          { percent:'30%', title:'应用验证与效果评估', text:'考察验证方案的科学性（如离线评估、回溯测试、交叉验证等），评估指标选择是否贴合业务场景，模型结果的可解释性及在真实环境下的鲁棒性表现。' },
          { percent:'20%', title:'产品思路与创新价值', text:'考察场景落地的可行性、产品方案的逻辑闭环与交互体验设计；考察问题解决视角的新颖性、方法论迁移或融合的创新度，以及对金融/业务真实场景的潜在提升价值与推广意义。' }
        ] }
      ]
    },
    internationalPanel () {
      const criteria = this.isEnglish ? [
        { percent:'40%', title:'Task Completion', text:'Completion of scripted tasks in a simulated environment for non-on-chain topics; Topic F is evaluated using on-chain transaction hashes.' },
        { percent:'30%', title:'Security & Compliance', text:'Defence against induced transfers, data extraction, privilege escalation and prompt injection; serious security failures trigger substantial deductions.' },
        { percent:'30%', title:'Innovation & Interaction', text:'Scenario novelty, real-world value, dialogue efficiency, clarification, correction and fallback capability.' }
      ] : [
        { percent:'40%', title:'任务完成率', text:'非链上方向以模拟环境中的脚本化任务完成度为准；方向 F 以真实链上交易哈希为依据。' },
        { percent:'30%', title:'安全合规性', text:'统一考察对诱导转账、信息套取、越权操作和 Prompt 注入的防御能力；严重安全问题将触发大幅扣分。' },
        { percent:'30%', title:'创新性及交互体验', text:'考察场景新颖性、真实问题解决度、轮次效率、追问、纠错和兜底能力。' }
      ]
      return { key:'international', index:'01', eyebrow:'INTERNATIONAL TRACK', title:this.isEnglish ? 'International Artificial Intelligence Track' : '人工智能赛道（国际赛道）', criteria, note:this.isEnglish ? 'A capped 0–5 point challenge/implementation bonus rewards advanced topics or demonstrated real-world deployment. Topic choice itself does not affect the base score. Appeals may be filed within three business days after results are announced.' : '设置 0—5 分“挑战/落地加分”（封顶），奖励主动挑战高阶方向或实现真实落地的团队；方向选择本身不影响基础评分。评审结果公布后 3 个工作日内可提交书面异议。' }
    }
  }
}
</script>

<style lang="scss" scoped>
.review-section { --accent:#315fd8; --soft:#eef4ff; padding:76px 0 90px; background:#f8f9fb; }
.theme-international { --accent:#078da9; --soft:#edf9fb; }
.section-heading { margin:0 0 38px; padding-bottom:22px; border-bottom:1px solid #e5e8ee; text-align:left; }
.section-heading h2 { color:#252b34; font-size:24px; font-weight:700; }
.section-heading > span { display:block; margin-top:8px; color:#858d9a; font-size:12px; line-height:1.7; }
.review-grid { display:grid; gap:0; background:#fff; align-items:stretch; }
.review-grid--split { grid-template-columns:repeat(2,minmax(0,1fr)); }
.review-panel { min-width:0; height:100%; box-sizing:border-box; padding:0 34px 26px; background:#fff; }
.review-panel + .review-panel { border-left:1px solid #e8ebf0; }
.review-panel > header { padding:22px 0; border-bottom:1px solid #eef0f4; }
.review-panel header h3 { color:#252b34; font-size:19px; font-weight:700; overflow-wrap:anywhere; word-break:break-word; }
.criteria-list { display:grid; grid-template-rows:repeat(3,minmax(0,1fr)); padding:5px 0 0; }
.criterion { display:grid; grid-template-columns:64px 1fr; gap:17px; min-height:150px; box-sizing:border-box; align-content:start; padding:23px 0; border-bottom:1px solid #edf0f5; }
.criterion:last-child { border-bottom:0; }
.criterion > strong { color:var(--accent); font-family:DIN-BlackItalic,DIN; font-size:23px; }
.criterion h4,.review-block h4 { color:#252b34; font-size:14px; }
.criterion p,.review-block p { margin-top:7px; color:#626b79; font-size:12.5px; line-height:1.85; overflow-wrap:anywhere; word-break:break-word; }
.review-block { margin:0; padding:23px 0; border-bottom:1px solid #edf0f5; background:#fff; }
.review-block.pending h4::after { content:'待确认'; margin-left:8px; padding:2px 6px; border-radius:999px; color:#a66c12; font-size:9px; background:#fff5df; }
.formula { margin-top:13px; padding:12px 14px; border-left:3px solid var(--accent); color:#34405a; font-size:12px; font-weight:700; line-height:1.65; background:var(--soft); }
.review-block ul { margin-top:10px; }
.review-block li { color:#738095; font-size:11px; line-height:1.75; }
.review-block li::before { content:'—'; margin-right:6px; color:var(--accent); }
.panel-note { margin:22px 0 0; padding:14px 16px; border-left:3px solid var(--accent); color:#58657b; font-size:11.5px; line-height:1.8; background:var(--soft); }
@include mobile {
  .review-section { padding:54px 0 64px; }
  .section-heading h2 { font-size:21px; }
  .review-grid--split { grid-template-columns:1fr; }
  .review-panel { padding:0 0 22px; }
  .review-panel + .review-panel { margin-top:24px; padding-top:5px; border-top:1px solid #e8ebf0; border-left:0; }
  .criteria-list { display:block; }
  .criterion { grid-template-columns:58px 1fr; min-height:0; }
}
</style>
